import COPY from "../content/siteCopy.json";
import React, { useState } from "react";
import { CalendlyLink } from "../SiteHeader";
import { MAIN_SITE_URL } from "../SiteSeo";

const tierFor = (tiers, s) => tiers.find((t) => s >= t[0]) || tiers[tiers.length - 1];

function ShareScore({ score, total, tier }) {
  const [state, setState] = useState("idle");
  const url = `${MAIN_SITE_URL}/mortgages/pre-approval/?score=${score}`;
  const img = `/brand/social/preapproval-score-${score}.png`;
  const text = `I scored ${score}/${total} on "Don't Blow Your Pre-Approval" — ${tier[1]} Think you can do better?`;
  const share = async () => {
    try {
      const blob = await fetch(img).then((r) => r.blob());
      const file = new File([blob], `preapproval-score-${score}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], title: text, text, url }); return; }
      if (navigator.share) { await navigator.share({ title: text, text, url }); return; }
      await navigator.clipboard.writeText(`${text} ${url}`); setState("copied"); setTimeout(() => setState("idle"), 2200);
    } catch (e) { if (e?.name !== "AbortError") setState("error"); }
  };
  const enc = encodeURIComponent;
  return (
    <div className="cg-share" data-testid="card-share">
      <span className="cg-share-k">Share your score</span>
      <div className="cg-share-row">
        <button type="button" className="btn outline" onClick={share} data-testid="card-share-native">{state === "copied" ? "Copied!" : state === "error" ? "Try again" : "Share my score card"}</button>
        <a className="cg-share-ic" href={`https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`} target="_blank" rel="noopener" aria-label="Share on X" data-testid="card-share-x">X</a>
        <a className="cg-share-ic" href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text)}`} target="_blank" rel="noopener" aria-label="Share on Facebook" data-testid="card-share-facebook">f</a>
        <a className="cg-share-ic" href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`} target="_blank" rel="noopener" aria-label="Share on LinkedIn" data-testid="card-share-linkedin">in</a>
        <a className="textlink cg-share-dl" href={img} download={`preapproval-score-${score}.png`} data-testid="card-share-download">Download the card</a>
      </div>
      <img src={img} alt={`Score card: ${score} out of ${total}`} className="cg-share-preview" loading="lazy" data-testid="card-share-preview" />
    </div>
  );
}

export function CardGame({ doc }) {
  const cards = doc?.game?.cards || [];
  const tiers = doc?.game?.tiers || [[0, "", ""]];
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState(null);
  if (!cards.length) return null;
  const over = i >= cards.length;
  const card = cards[i];
  const answer = (v) => { if (picked != null) return; setPicked(v); if (v === card.safe) setScore((s) => s + 1); };
  const next = () => { setPicked(null); setI(i + 1); };
  const reset = () => { setI(0); setScore(0); setPicked(null); };
  const correct = picked != null && picked === card?.safe;
  const tier = tierFor(tiers, score);
  return (
    <section className="cg" data-testid="card-game">
      <div className="wrap">
        <div className="cg-head">
          <div className="eyebrow">The game · between pre-approval and keys</div>
          <h2 className="display cg-h2">Don't blow your <em>pre-approval.</em></h2>
          <p className="cg-lead">{COPY.solution.extras["pre-approval"][2].text}</p>
        </div>
        {!over ? (
          <div className="cg-stage">
            <div className="cg-meta"><span data-testid="card-progress">Card {i + 1} of {cards.length}</span><span data-testid="card-score">Score {score}</span></div>
            <div className={`cg-card${picked != null ? " flipped" : ""}`} data-testid="card-current">
              <div className="cg-face cg-front">
                <span className="cg-n">{String(i + 1).padStart(2, "0")}</span>
                <p>{card.q}</p>
                <div className="cg-btns">
                  <button type="button" className="cg-btn safe" onClick={() => answer(true)} data-testid="card-safe">Safe move</button>
                  <button type="button" className="cg-btn risky" onClick={() => answer(false)} data-testid="card-risky">Risky move</button>
                </div>
              </div>
              <div className="cg-face cg-back">
                <img src={card.safe ? "/brand/how-step-approved.png" : "/brand/preapproval-facepalm.png"} alt="" className={`cg-mascot${card.safe ? " ok" : ""}`} />
                <div>
                  <span className={`cg-verdict ${card.safe ? "safe" : "risky"}`} data-testid="card-verdict">{card.safe ? "Safe move" : "Risky move"} · {correct ? "you nailed it" : "you'd have blown it"}</span>
                  <p>{card.why}</p>
                  <button type="button" className="btn" onClick={next} data-testid="card-next">{i + 1 < cards.length ? "Next card →" : "See my score"}</button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="cg-end" data-testid="card-end">
            <img src={score >= 7 ? "/brand/stackup-trophy.png" : "/brand/preapproval-facepalm.png"} alt="" className="cg-end-art" />
            <div>
              <div className="eyebrow">Final score</div>
              <h3 className="display cg-score" data-testid="card-final-score">{score}<small>/{cards.length}</small> — {tier[1]}</h3>
              <p>{tier[2]} The real version of this game has money on the line—so we hand every pre-approved client a short "don't do this yet" list and stay on text until closing.</p>
              <div className="cg-end-btns"><CalendlyLink className="btn" testId="card-cta">Get the real thing</CalendlyLink><button type="button" className="textlink pq-again" onClick={reset} data-testid="card-replay">Play again</button></div>
              <ShareScore score={score} total={cards.length} tier={tier} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
